import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, Play, Volume2, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { vocabularyAPI, categoriesAPI, progressAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { usePronunciation } from '../../hooks/usePronunciation';

const AudioPronunciation = () => {
  const [currentWord, setCurrentWord] = useState(0);
  const [score, setScore] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [userRating, setUserRating] = useState(null);
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gameComplete, setGameComplete] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { speak, isSupported: speechSupported, isLoading: speechLoading } = usePronunciation();

  useEffect(() => {
    loadVocabulary();
    loadCategories();
  }, []);

  const loadVocabulary = async () => {
    try {
      setLoading(true);
      const response = await vocabularyAPI.getAll();
      // Filter words that have pronunciation guides
      const wordsWithPronunciation = (response.words || []).filter(word => word.pronunciation);
      setWords(wordsWithPronunciation);
    } catch (error) {
      toast({
        title: "Error Loading Vocabulary",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await categoriesAPI.getAll();
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const currentWordData = words[currentWord];

  const playPronunciation = async () => {
    if (!currentWordData) return;

    setIsPlaying(true);
    
    try {
      // Try to play custom audio file first
      if (currentWordData.audioUrl) {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        // Add cache-busting parameter to ensure we get the latest audio file
        const cacheBuster = `?t=${Date.now()}`;
        const audioUrl = currentWordData.audioUrl.startsWith('http') 
          ? `${currentWordData.audioUrl}${cacheBuster}`
          : `${API_URL}${currentWordData.audioUrl}${cacheBuster}`;
        
        toast({
          title: "🔊 Playing pronunciation",
          description: `Listen to: ${currentWordData.algerianWord} (${currentWordData.pronunciation})`,
        });

        const audio = new Audio();
        audio.crossOrigin = "anonymous"; // Enable CORS
        audio.src = audioUrl;
        
        // Wait for audio to load before playing
        await new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = (e) => {
            console.error('Audio error:', e);
            reject(new Error('Failed to load audio file'));
          };
          audio.onloadeddata = () => {
            audio.play().catch(reject);
          };
          audio.load();
        });
      } else {
        // Fallback to text-to-speech if no audio file
        if (!speechSupported) {
          toast({
            title: "Pronunciation Guide",
            description: `${currentWordData.algerianWord}: ${currentWordData.pronunciation || 'No guide available'}`,
          });
          setIsPlaying(false);
          return;
        }

        if (speechLoading) {
          toast({
            title: "Loading voices...",
            description: "Please wait for speech system to initialize",
          });
          setIsPlaying(false);
          return;
        }

        toast({
          title: "🔊 Playing pronunciation",
          description: `Listen to: ${currentWordData.algerianWord} (${currentWordData.pronunciation})`,
        });

        await speak(currentWordData.algerianWord, { rate: 0.7, pitch: 1, volume: 1 });
      }
    } catch (error) {
      console.error('Pronunciation failed:', error);
      toast({
        title: "Pronunciation Error",
        description: `${currentWordData.algerianWord}: ${currentWordData.pronunciation || 'Audio not available'}`,
        variant: "destructive"
      });
    } finally {
      setIsPlaying(false);
    }
  };

  const showWordTranslation = () => {
    setShowTranslation(true);
  };

  const rateAttempt = (rating) => {
    setUserRating(rating);
    
    if (rating >= 3) {
      setScore(score + 1);
      toast({
        title: "Great job!",
        description: rating === 5 ? "Perfect pronunciation! 🎉" : "Good attempt! Keep practicing! 👍",
      });
    } else {
      toast({
        title: "Keep trying!",
        description: "Practice makes perfect. Try again! 💪",
        variant: "destructive"
      });
    }
  };

  const nextWord = async () => {
    // Update progress for current word
    const currentWordData = words[currentWord];
    const ratingScore = userRating ? userRating / 5 : 0; // Convert 1-5 rating to 0-1 score
    
    if (currentWordData) {
      try {
        await progressAPI.update({
          category: currentWordData.category,
          wordId: currentWordData.id,
          gameType: "pronunciation",
          score: ratingScore,
          completed: userRating !== null
        });
      } catch (error) {
        console.error('Failed to update progress:', error);
      }
    }
    
    if (currentWord < words.length - 1) {
      setCurrentWord(currentWord + 1);
      setShowTranslation(false);
      setUserRating(null);
    } else {
      setGameComplete(true);
      toast({
        title: "Practice Complete!",
        description: `You successfully pronounced ${score}/${words.length} words!`,
      });
    }
  };

  const resetPractice = () => {
    setCurrentWord(0);
    setScore(0);
    setShowTranslation(false);
    setUserRating(null);
    setGameComplete(false);
  };

  const getCategoryInfo = () => {
    if (!currentWordData) return null;
    return categories.find(cat => cat.id === currentWordData.category);
  };

  const progress = words.length > 0 ? ((currentWord + 1) / words.length) * 100 : 0;

  if (words.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">No pronunciation guides available</h1>
        <p className="text-gray-600 mb-4">Add pronunciation guides to vocabulary words to use this feature.</p>
        <Link to="/vocabulary">
          <Button>Manage Vocabulary</Button>
        </Link>
      </div>
    );
  }

  if (gameComplete) {
    const percentage = Math.round((score / words.length) * 100);
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <Card className="max-w-md mx-auto shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Practice Complete!</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-4xl font-bold text-green-600 mb-4">
                  {score}/{words.length}
                </div>
                <div className="text-2xl font-semibold text-gray-700 mb-6">
                  {percentage}% Success Rate
                </div>
                
                <div className="text-lg mb-6">
                  {percentage >= 80 ? "Excellent pronunciation! 🎉" : 
                   percentage >= 60 ? "Good progress! Keep practicing 👍" : 
                   "Keep practicing to improve! 💪"}
                </div>
                
                <div className="flex space-x-4">
                  <Button onClick={resetPractice} className="flex-1">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Practice Again
                  </Button>
                  <Link to="/games" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Games
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <Link to="/games">
          <Button variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Games
          </Button>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Pronunciation Practice</h1>
        <Button variant="outline" size="sm" onClick={resetPractice}>
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
      </div>

      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">
            Word {currentWord + 1} of {words.length}
          </span>
          <span className="text-sm text-gray-600">
            Correct: {score}/{words.length}
          </span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Instructions */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 text-blue-800">
            <Volume2 className="w-5 h-5" />
            <p className="text-sm">
              <strong>How to practice:</strong> Listen to the pronunciation, try to repeat it, then rate your attempt. Click "Show Translation" if you need help understanding the word.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Word Card */}
      <Card className="mb-8 shadow-lg">
        <CardHeader className="text-center">
          {getCategoryInfo() && (
            <Badge 
              variant="secondary" 
              className="mb-2 self-center"
              style={{ 
                backgroundColor: `${getCategoryInfo()?.color}20`, 
                color: getCategoryInfo()?.color 
              }}
            >
              {getCategoryInfo()?.icon} {getCategoryInfo()?.name}
            </Badge>
          )}
          <CardTitle className="text-3xl text-gray-900 mb-2">
            {currentWordData.algerianWord}
          </CardTitle>
          <p className="text-lg text-gray-500 italic">
            {currentWordData.pronunciation}
          </p>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {/* Audio Controls */}
          <div className="space-y-4">
            <Button 
              size="lg" 
              onClick={playPronunciation}
              disabled={isPlaying}
              className="bg-gradient-to-r from-green-600 to-red-600 hover:from-green-700 hover:to-red-700 text-white"
            >
              {isPlaying ? (
                <>
                  <Volume2 className="w-5 h-5 mr-2 animate-pulse" />
                  Playing...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Listen to Pronunciation
                </>
              )}
            </Button>

            {!showTranslation && (
              <Button variant="outline" onClick={showWordTranslation}>
                Show Translation
              </Button>
            )}
          </div>

          {/* Translation */}
          {showTranslation && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-xl font-semibold text-green-800 mb-2">
                {currentWordData.englishTranslation}
              </h3>
              {currentWordData.exampleSentence && (
                <p className="text-green-700 italic">
                  "{currentWordData.exampleSentence}"
                </p>
              )}
            </div>
          )}

          {/* Rating */}
          {userRating === null ? (
            <div className="space-y-3">
              <p className="text-gray-700 font-medium">How did you do?</p>
              <div className="flex justify-center space-x-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <Button
                    key={rating}
                    variant="outline"
                    size="sm"
                    onClick={() => rateAttempt(rating)}
                    className="w-12 h-12 rounded-full hover:bg-yellow-50 hover:border-yellow-300"
                  >
                    {rating}⭐
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                1 = Need more practice, 5 = Perfect!
              </p>
            </div>
          ) : (
            <div className={`p-4 rounded-lg ${
              userRating >= 3 ? 'bg-green-100 border border-green-300' : 'bg-orange-100 border border-orange-300'
            }`}>
              <div className="flex items-center justify-center space-x-2 mb-2">
                {userRating >= 3 ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-orange-600" />
                )}
                <span className={`font-semibold ${
                  userRating >= 3 ? 'text-green-800' : 'text-orange-800'
                }`}>
                  You rated yourself: {userRating}⭐
                </span>
              </div>
              <p className={`text-sm ${
                userRating >= 3 ? 'text-green-700' : 'text-orange-700'
              }`}>
                {userRating === 5 ? "Perfect pronunciation!" :
                 userRating === 4 ? "Very good!" :
                 userRating === 3 ? "Good attempt!" :
                 userRating === 2 ? "Keep trying!" :
                 "Practice more!"}
              </p>
            </div>
          )}

          {/* Next Button */}
          {userRating !== null && (
            <Button onClick={nextWord} size="lg">
              {currentWord < words.length - 1 ? 'Next Word' : 'View Results'}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <div className="text-center text-gray-500 text-sm">
        <p>💡 Tips: Listen carefully, repeat slowly, and don't worry about being perfect!</p>
      </div>
    </div>
  );
};

export default AudioPronunciation;